"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import "@/styles/components/footer.scss";
import dynamic from "next/dynamic";
import Cookies from "js-cookie";
import {
  FaFacebookF,
  FaTiktok,
  FaYoutube,
  FaLinkedinIn,
  FaXTwitter,
  FaThreads,
  FaInstagram,
} from "react-icons/fa6";

const ReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
  ssr: false,
});

// ─── Locale Content ───────────────────────────────────────────────────────────

const getLocaleContent = (locale) => {
  switch (locale) {
    case "ar-IQ":
      return {
        explore: "استكشف",
        legal: "قانوني",
        helpCenter: "مركز المساعدة",
        content: "المحتوى",
        contactUs: "اتصل بنا",
        getInTouch: "تواصل معنا",
        videoTutorials: "دروس الفيديو",
        namePlaceholder: "الاسم",
        emailPlaceholder: "البريد الإلكتروني",
        phonePlaceholder: "الهاتف",
        subjectPlaceholder: "الموضوع",
        messagePlaceholder: "الرسالة",
        submit: "إرسال",
        sending: "جارٍ الإرسال...",
        successMessage: "تم إرسال الرسالة بنجاح!",
        errorCaptcha: "يرجى التحقق من أنك لست روبوتًا",
        errorFallback: "فشل في إرسال الرسالة. يرجى المحاولة مرة أخرى.",
        copyright: `حقوق النشر © ${new Date().getFullYear()} Baskitt. جميع الحقوق محفوظة`,
        proudlyMade:
          "صُنع بفخر في الولايات المتحدة الأمريكية بواسطة DigitalEFX. نقدم جودة يمكنك الوثوق بها",
        privacyPolicy: "سياسة الخصوصية",
        customerDeletion: "سياسة حذف العملاء",
        termsOfUse: "شروط الاستخدام",
        refundPolicy: "سياسة الاسترداد",
        cantBeShipped: "لا يمكن شحنه",
        faqs: "الأسئلة الشائعة",
        blogs: "المدونات",
        exploreLinks: {
          main: {
            left: [
              { title: "تحميل تطبيق Baskitt", href: "/#hero_section" },
              { title: "لماذا نحن", href: "/#features_section" },
              { title: "معلومات عنا", href: "/#about_us_section" },
              { title: "الأكثر استخدامًا", href: "/#most_use_section" },
              { title: "الميزات الرئيسية", href: "/#key_features_section" },
            ],
            right: [
              { title: "الشهادات", href: "/#testimonials" },
              { title: "دروس تعليمية", href: "/#product_tutorials" },
              { title: "التكامل", href: "/#integration" },
              { title: "المناطق", href: "/#located_section" },
              { title: "اتصل بنا", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "تحميل تطبيق Baskitt", href: "/#hero_section" },
              { title: "لماذا نحن", href: "/#why_us_section" },
              { title: "حاسبة الشحن", href: "/#shipping_calculator" },
              { title: "مواقعنا", href: "/#select_locations" },
              { title: "ميزات التطبيق", href: "/#app_features_section" },
            ],
            right: [
              { title: "كيف يعمل", href: "/#how_it_works_section" },
              { title: "الميزات الرئيسية", href: "/#features_section" },
              { title: "طرق الدفع", href: "/#payment_methods_section" },
              { title: "اتصل بنا", href: "/#contact-us" },
            ],
          },
        },
      };

    case "ckb":
      return {
        explore: "گەڕانەوە",
        legal: "یاسایی",
        helpCenter: "ناوەندی یارمەتی",
        content: "ناوەڕۆک",
        contactUs: "پەیوەندیمان پێوە بکە",
        getInTouch: "پەیوەندی بکە",
        namePlaceholder: "ناو",
        videoTutorials: "وانە ڤیدیۆییەکان",
        emailPlaceholder: "ئیمەیڵ",
        phonePlaceholder: "تەلەفۆن",
        subjectPlaceholder: "بابەت",
        messagePlaceholder: "پەیام",
        submit: "ناردن",
        sending: "دەنێردرێت...",
        successMessage: "پەیام بە سەرکەوتوویی نێردرا!",
        errorCaptcha: "تکایە دڵنیابەرەوە کە ئادەمیزادەیت نەک ڕۆبۆت",
        errorFallback:
          "شکستی هێنا لە ناردنی پەیام. تکایە دووبارە هەوڵ بدەرەوە.",
        copyright: `مافی چاپ © ${new Date().getFullYear()} Baskitt. هەموو مافەکان پارێزراون`,
        proudlyMade:
          "بە شانازی لە ئەمریکا دروستکراوە لەلایەن DigitalEFX. کوالیتی باوەڕپێکراو پێشکەش دەکەین",
        privacyPolicy: "سیاسەتی تایبەتمەندی",
        customerDeletion: "سیاسەتی سڕینەوەی کڕیار",
        termsOfUse: "مەرجەکانی بەکارهێنان",
        refundPolicy: "سیاسەتی گەڕاندنەوەی پارە",
        cantBeShipped: "ناتوانرێت بنێردرێت",
        faqs: "پرسیارە باوەکان",
        blogs: "بلۆگەکان",
        exploreLinks: {
          main: {
            left: [
              { title: "داونلۆدی ئەپی Baskitt", href: "/#hero_section" },
              { title: "بۆچی ئێمە", href: "/#features_section" },
              { title: "دەربارەی ئێمە", href: "/#about_us_section" },
              { title: "زۆرترین بەکارهێنان", href: "/#most_use_section" },
              {
                title: "تایبەتمەندییە سەرەکیەکان",
                href: "/#key_features_section",
              },
            ],
            right: [
              { title: "شایەتیەکان", href: "/#testimonials" },
              { title: "وانەکان", href: "/#product_tutorials" },
              { title: "یەکگرتن", href: "/#integration" },
              { title: "ناوچەکان", href: "/#located_section" },
              { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "داونلۆدی ئەپی Baskitt", href: "/#hero_section" },
              { title: "بۆچی ئێمە", href: "/#why_us_section" },
              { title: "حیساب کردنی گەیاندن", href: "/#shipping_calculator" },
              { title: "شوێنەکانمان", href: "/#select_locations" },
              {
                title: "تایبەتمەندییەکانی ئەپ",
                href: "/#app_features_section",
              },
            ],
            right: [
              { title: "چۆن کار دەکات", href: "/#how_it_works_section" },
              { title: "تایبەتمەندییە سەرەکیەکان", href: "/#features_section" },
              {
                title: "شێوازەکانی پارەدان",
                href: "/#payment_methods_section",
              },
              { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
            ],
          },
        },
      };
    case "sdh":
      return {
        explore: "گەڕان",
        legal: "یاسایی",
        helpCenter: "ناوەندی یارمەتی",
        content: "ناوەڕۆک",
        contactUs: "پەیوەندیمان پێوە بکە",
        getInTouch: "پەیوەندی بکە",
        namePlaceholder: "ناو",
        emailPlaceholder: "ئیمەیڵ",
        phonePlaceholder: "ژمارەی تەلەفۆن",
        subjectPlaceholder: "بابەت",
        messagePlaceholder: "پەیام",
        videoTutorials: "فێرکاریە ڤیدیۆییەکان",
        submit: "ناردن",
        sending: "لە ناردندایە...",
        successMessage: "پەیام بە سەرکەوتوویی نێردرا!",
        errorCaptcha: "تکایە دڵنیابەرەوە کە تۆ مرۆڤیت نەک ڕۆبۆت",
        errorFallback:
          "ناردنی پەیام سەرکەوتوو نەبوو. تکایە دووبارە هەوڵ بدەرەوە.",
        copyright: `مافی چاپ © ${new Date().getFullYear()} Baskitt. هەموو مافەکان پارێزراون`,
        proudlyMade:
          "بە شانازی لە ئەمریکا دروستکراوە لەلایەن DigitalEFX. کوالیتی باوەڕپێکراو پێشکەش دەکەین",
        privacyPolicy: "سیاسەتی تایبەتمەندی",
        customerDeletion: "سیاسەتی سڕینەوەی کڕیار",
        termsOfUse: "مەرجەکانی بەکارهێنان",
        refundPolicy: "سیاسەتی گەڕاندنەوەی پارە",
        cantBeShipped: "ناتوانرێت بنێردرێت",
        faqs: "پرسیارە باوەکان",
        blogs: "بلۆگ",
        exploreLinks: {
          main: {
            left: [
              { title: "داگرتنی ئەپی Baskitt", href: "/#hero_section" },
              { title: "بۆچی ئێمە", href: "/#features_section" },
              { title: "دەربارەی ئێمە", href: "/#about_us_section" },
              { title: "زۆرترین بەکارهێنان", href: "/#most_use_section" },
              {
                title: "تایبەتمەندییە سەرەکیەکان",
                href: "/#key_features_section",
              },
            ],
            right: [
              { title: "شایەتیەکان", href: "/#testimonials" },
              { title: "فێرکاریەکان", href: "/#product_tutorials" },
              { title: "یەکگرتن", href: "/#integration" },
              { title: "ناوچەکان", href: "/#located_section" },
              { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "داگرتنی ئەپی Baskitt", href: "/#hero_section" },
              { title: "بۆچی ئێمە", href: "/#why_us_section" },
              { title: "ژمێرەری گواستنەوە", href: "/#shipping_calculator" },
              { title: "شوێنەکانمان", href: "/#select_locations" },
              {
                title: "تایبەتمەندییەکانی ئەپ",
                href: "/#app_features_section",
              },
            ],
            right: [
              { title: "چۆن کار دەکات", href: "/#how_it_works_section" },
              { title: "تایبەتمەندییە سەرەکیەکان", href: "/#features_section" },
              {
                title: "شێوازەکانی پارەدان",
                href: "/#payment_methods_section",
              },
              { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
            ],
          },
        },
      };
    case "es":
      return {
        explore: "Explorar",
        legal: "Legal",
        helpCenter: "Centro de Ayuda",
        content: "Contenido",
        contactUs: "Contáctenos",
        getInTouch: "Ponerse en contacto",
        namePlaceholder: "Nombre",
        emailPlaceholder: "Correo electrónico",
        phonePlaceholder: "Teléfono",
        subjectPlaceholder: "Asunto",
        messagePlaceholder: "Mensaje",
        submit: "Enviar",
        sending: "Enviando...",
        successMessage: "¡Mensaje enviado con éxito!",
        errorCaptcha: "Por favor, verifica que no eres un robot",
        videoTutorials: "Tutoriales en Video",
        errorFallback:
          "Error al enviar el mensaje. Por favor, inténtalo de nuevo.",
        copyright: `Copyright © ${new Date().getFullYear()} Baskitt. Todos los derechos reservados.`,
        proudlyMade:
          "Hecho con orgullo en EE.UU. por DigitalEFX. Ofreciendo calidad en la que puedes confiar",
        privacyPolicy: "Política de Privacidad",
        customerDeletion: "Política de Eliminación de Clientes",
        termsOfUse: "Términos de Uso",
        refundPolicy: "Política de Reembolso",
        cantBeShipped: "No se puede enviar",
        faqs: "Preguntas Frecuentes",
        blogs: "Blogs",
        exploreLinks: {
          main: {
            left: [
              { title: "Descargar App Baskitt", href: "/#hero_section" },
              { title: "Por Qué Nosotros", href: "/#features_section" },
              { title: "Sobre Nosotros", href: "/#about_us_section" },
              { title: "Más Usado", href: "/#most_use_section" },
              {
                title: "Características Clave",
                href: "/#key_features_section",
              },
            ],
            right: [
              { title: "Testimonios", href: "/#testimonials" },
              { title: "Tutoriales", href: "/#product_tutorials" },
              { title: "Integración", href: "/#integration" },
              { title: "Zonas", href: "/#located_section" },
              { title: "Contáctenos", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "Descargar App Baskitt", href: "/#hero_section" },
              { title: "Por Qué Nosotros", href: "/#why_us_section" },
              { title: "Calculadora de Envío", href: "/#shipping_calculator" },
              { title: "Nuestras Ubicaciones", href: "/#select_locations" },
              { title: "Funciones de la App", href: "/#app_features_section" },
            ],
            right: [
              { title: "Cómo Funciona", href: "/#how_it_works_section" },
              { title: "Características Clave", href: "/#features_section" },
              { title: "Métodos de Pago", href: "/#payment_methods_section" },
              { title: "Contáctenos", href: "/#contact-us" },
            ],
          },
        },
      };

    default:
      return {
        explore: "Explore",
        legal: "Legal",
        helpCenter: "Help Center",
        videoTutorials: "Video Tutorials",
        content: "Content",
        contactUs: "Contact us",
        getInTouch: "Get in touch",
        namePlaceholder: "Name",
        emailPlaceholder: "Email",
        phonePlaceholder: "Phone",
        subjectPlaceholder: "Subject",
        messagePlaceholder: "Message",
        submit: "Submit",
        sending: "Sending...",
        successMessage: "Message sent successfully!",
        errorCaptcha: "Please verify that you are not a robot",
        errorFallback: "Failed to send message. Please try again.",
        copyright: `Copyright © ${new Date().getFullYear()} Baskitt. All rights reserved.`,
        proudlyMade:
          "Proudly made in the USA 🇺🇸 by DigitalEFX. Delivering quality you can trust",
        privacyPolicy: "Privacy Policy",
        customerDeletion: "Customer Deletion Policy",
        termsOfUse: "Terms of Use",
        refundPolicy: "Refund Policy",
        cantBeShipped: "Can't be Shipped",
        faqs: "FAQs",
        blogs: "Blogs",
        exploreLinks: {
          main: {
            left: [
              { title: "Download Baskitt App", href: "/#hero_section" },
              { title: "Why Us", href: "/#features_section" },
              { title: "About Us", href: "/#about_us_section" },
              { title: "Most Use", href: "/#most_use_section" },
              { title: "Key Features", href: "/#key_features_section" },
            ],
            right: [
              { title: "Testimonials", href: "/#testimonials" },
              { title: "Tutorials", href: "/#product_tutorials" },
              { title: "Integration", href: "/#integration" },
              { title: "Zones", href: "/#located_section" },
              { title: "Contact Us", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "Download Baskitt App", href: "/#hero_section" },
              { title: "Why Us", href: "/#why_us_section" },
              { title: "Shipping Calculator", href: "/#shipping_calculator" },
              { title: "Our Locations", href: "/#select_locations" },
              { title: "App Features", href: "/#app_features_section" },
            ],
            right: [
              { title: "How It Works", href: "/#how_it_works_section" },
              { title: "Key Features", href: "/#features_section" },
              { title: "Payment Methods", href: "/#payment_methods_section" },
              { title: "Contact Us", href: "/#contact-us" },
            ],
          },
        },
      };
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

const Footer = ({ content }) => {
  const recaptchaRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [subdomain, setSubdomain] = useState(null);
  const locale = Cookies.get("language") || "en";
  const t = getLocaleContent(locale);

  useEffect(() => {
    const host = window.location.hostname;
    const parts = host.split(".");
    setSubdomain(parts.length > 1 ? parts[0] : null);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaptchaChange = (token) => {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const captchaToken = recaptchaRef.current.getValue();

      if (!captchaToken) {
        setStatus({ type: "error", message: t.errorCaptcha });
        setIsSubmitting(false);
        return;
      }

      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, captchaToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t.errorFallback);
      }

      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      recaptchaRef.current.reset();
      setStatus({ type: "success", message: t.successMessage });
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: error.message || t.errorFallback });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentExplore = subdomain
    ? t.exploreLinks.subdomain
    : t.exploreLinks.main;

  // RTL locales
  const isRtl = locale === "ar-IQ" || locale === "ckb";

  return (
    <footer id="footer" dir={isRtl ? "rtl" : "ltr"}>
      <div className="footer_container">
        <div className="footer_content">
          {isMobile && (
            <div className="footer_form" id="contact-us">
              <div className="title_wrapper">
                <h3 className="subtitle">{t.contactUs}</h3>
                <h2 className="title">{t.getInTouch}</h2>
              </div>

              <form id="footer_contact_form" onSubmit={handleSubmit}>
                <div className="row_container">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.namePlaceholder}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.emailPlaceholder}
                    required
                  />
                </div>

                <div className="row_container">
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t.phonePlaceholder}
                    required
                  />
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t.subjectPlaceholder}
                    required
                  />
                </div>

                <div className="row_container">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.messagePlaceholder}
                    required
                  />
                </div>

                <div className="captcha_container">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    size={isMobile ? "compact" : "normal"}
                  />
                </div>

                {status.message && (
                  <div className={`status-message ${status.type}`}>
                    {status.message}
                  </div>
                )}

                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t.sending : t.submit}
                </button>
              </form>
            </div>
          )}
          <div className="footer_links_wrapper">
            {/* EXPLORE */}
            <div className="footer_links explore">
              <div className="footer_logo">
                <Image
                  src="/images/logo.png"
                  width={375}
                  height={132}
                  alt="logo"
                />
                {isMobile && (
                  <div className="copyright">
                    Copyright © 2026. All rights reserved 
                  </div>
                )}
              </div>
              <h3>{t.explore}</h3>

              <div className="explore_grid">
                <div className="links">
                  {currentExplore.left.map((link, i) => (
                    <Link key={i} href={link.href}>
                      {link.title}
                    </Link>
                  ))}
                </div>

                <div className="links">
                  {currentExplore.right.map((link, i) => (
                    <Link key={i} href={link.href}>
                      {link.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* CONTACT FORM */}
            {!isMobile && (
              <div className="footer_form" id="contact-us">
                <div className="title_wrapper">
                  <h3 className="subtitle">{t.contactUs}</h3>
                  <h2 className="title">{t.getInTouch}</h2>
                </div>

                <form id="footer_contact_form" onSubmit={handleSubmit}>
                  <div className="row_container">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t.namePlaceholder}
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t.emailPlaceholder}
                      required
                    />
                  </div>

                  <div className="row_container">
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t.phonePlaceholder}
                      required
                    />
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder={t.subjectPlaceholder}
                      required
                    />
                  </div>

                  <div className="row_container">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t.messagePlaceholder}
                      required
                    />
                  </div>

                  <div className="captcha_container">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                      onChange={handleCaptchaChange}
                      size={isMobile ? "compact" : "normal"}
                    />
                  </div>

                  {status.message && (
                    <div className={`status-message ${status.type}`}>
                      {status.message}
                    </div>
                  )}

                  <button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t.sending : t.submit}
                  </button>
                </form>
              </div>
            )}

            {/* LEGAL */}
            <div className="footer_links">
              <div className="footer_logo_gap" />
              <h3>{t.legal}</h3>
              <Link href="/privacy-policy">{t.privacyPolicy}</Link>
              <Link href="/customer-deletion-policy">{t.customerDeletion}</Link>
              <Link href="/terms-of-use">{t.termsOfUse}</Link>
              <Link href="/refund-policy">{t.refundPolicy}</Link>
              <Link href="/shipping-restrictions">{t.cantBeShipped}</Link>
              {!isMobile && (
                <div className="footer_socials">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <FaFacebookF color="#fff" />
                  </a>

                  <a
                    href="https://tiktok.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="TikTok"
                  >
                    <FaTiktok color="#fff" />
                  </a>

                  <a
                    href="https://youtube.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                  >
                    <FaYoutube color="#fff" />
                  </a>

                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                  >
                    <FaLinkedinIn color="#fff" />
                  </a>

                  <a
                    href="https://x.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="X"
                  >
                    <FaXTwitter color="#fff" />
                  </a>

                  <a
                    href="https://threads.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Threads"
                  >
                    <FaThreads color="#fff" />
                  </a>

                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <FaInstagram color="#fff" />
                  </a>
                </div>
              )}
            </div>

            {/* HELP CENTER + CONTENT */}
            <div className="footer_links help_center">
              <div className="footer_logo_gap" />
              <h3>{t.helpCenter}</h3>
              <Link href="/faqs">{t.faqs}</Link>
              <Link href="/tutorials">{t.videoTutorials}</Link>
              <h3 className="mt">{t.content}</h3>
              <Link href="/blogs">{t.blogs}</Link>
            </div>
          </div>
        </div>
      </div>
      {isMobile && (
        <div className="footer_socials">
          <a
            href="https://facebook.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FaFacebookF color="#fff" />
          </a>

          <a
            href="https://tiktok.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok"
          >
            <FaTiktok color="#fff" />
          </a>

          <a
            href="https://youtube.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
          >
            <FaYoutube color="#fff" />
          </a>

          <a
            href="https://linkedin.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <FaLinkedinIn color="#fff" />
          </a>

          <a
            href="https://x.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X"
          >
            <FaXTwitter color="#fff" />
          </a>

          <a
            href="https://threads.net"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Threads"
          >
            <FaThreads color="#fff" />
          </a>

          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <FaInstagram color="#fff" />
          </a>
        </div>
      )}
      <div
        className="footer_made_by"
        dangerouslySetInnerHTML={{
          __html: t.proudlyMade.replace(
            "DigitalEFX",
            ' <a href="#">DigitalEFX</a> ',
          ),
        }}
      />
    </footer>
  );
};

export default Footer;
